import { useForm } from 'react-hook-form'
import { useModalStore } from '@/store/useModal'
import {
  SummaryDataProps,
  SummaryPropsResponse,
} from './SummaryAppointment.types'
import {
  useCreateAppointment,
  useGetResponsibleList,
  useUpdaterContact,
} from '@/modules/Telemedicine/queries/ScheduleAppointment/Telemedicine.queries'
import { useCallback, useMemo, useState } from 'react'
import { createAppointmentExceptionValidate } from '@/modules/Telemedicine/validations/createAppointmentExceptionValidate/createAppointmentExceptionValidate'
import { useYupValidationResolver } from '@/hooks/useYupValidationResolver'
import { validateEmail } from '@/utils/validateEmail'
import { validatePhone } from '@/utils/validatePhone'

export const useSummaryAppointment = ({
  isException,
  summaryData,
}: SummaryPropsResponse) => {
  const [fields, setFields] = useState<SummaryDataProps>(summaryData)

  const emailValidate = useMemo(
    () => validateEmail(fields?.patient?.patientEmail),
    [fields?.patient?.patientEmail],
  )
  const phoneValidate = useMemo(
    () => validatePhone(fields?.patient?.patientPhone),
    [fields?.patient?.patientPhone],
  )
  const enableButton = useMemo(
    () => emailValidate && phoneValidate,
    [emailValidate, phoneValidate],
  )

  const openModal = useModalStore(state => state.modalOpen)
  const closeModal = useModalStore(state => state.closeModal)

  const { createAppointment, createAppointmentIsLoading } =
    useCreateAppointment()
  const { responsibleList } = useGetResponsibleList()

  const { updaterContact } = useUpdaterContact()
  const handleSubmitForm = useCallback(async () => {
    const payload = {
      data: {
        email: fields?.patient?.patientEmail,
        celular: fields?.patient?.patientPhone,
      },
      patientId: fields?.patient?.patientId,
    }
    updaterContact.mutateAsync(payload)
  }, [
    fields?.patient?.patientEmail,
    fields?.patient?.patientId,
    fields?.patient?.patientPhone,
    updaterContact,
  ])

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm({
    resolver: useYupValidationResolver({
      schemaFunction: () =>
        createAppointmentExceptionValidate({
          isException,
        }),
    }),
  })

  const formException = watch()

  const formExceptionDisabled = useMemo(() => {
    return (
      !formException?.responsible?.id ||
      !formException?.exceptionDate ||
      !formException?.exceptionReason
    )
  }, [
    formException?.exceptionDate,
    formException?.exceptionReason,
    formException?.responsible?.id,
  ])

  const onSubmit = async (data: SummaryDataProps) => {
    const payload = {
      patientId: data?.patient?.patientId,
      professionalId: data?.professional?.professionalId,
      appointmentRecurrenceParams: {
        startDate: `${data?.appointmentDate} ${data?.appointmentHour}`,
        fittingAppointment: data?.fitIn,
        appointmentObject: data?.appointmentObject?.key,
        appointmentPvId: data?.pvAvailabilityId,
        appointmentException: {
          reponsibleUserId: formException?.responsible?.id,
          authorizationDate: formException?.exceptionDate,
          exceptionReason: formException?.exceptionReason,
        },
      },
      healthInsuranceId: data?.healthInsuranceData?.healthInsuranceId,
      multipleHealthInsuranceCards:
        data?.healthInsuranceData?.multipleHealthInsuranceCards,
    }

    try {
      createAppointment.mutate(payload)
      await handleSubmitForm()
    } catch (error) {
      console.error('Erro ao criar o compromisso:', error)
    }
  }

  return {
    openModal,
    createAppointmentIsLoading,
    control,
    fields,
    responsibleList,
    formExceptionDisabled,
    errors,
    emailValidate,
    phoneValidate,
    setFields,
    enableButton,
    onSubmit,
    closeModal,
    handleSubmit,
  }
}
